import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { messageService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import type { Message } from '@/types'
import { PageHeader, LoadingState, PageContainer } from '@/components/layout'
import { MessageStatus } from '@/types/enums'
import { MessageForm, type MessageFormData } from '@/components/messages/MessageForm'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'

export default function MessageEditPage() {
  const { messageId } = useParams<{ messageId: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'unread'
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
  const [secondaryActionLoading, setSecondaryActionLoading] = useState(false)
  const [message, setMessage] = useState<Message | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  useEffect(() => {
    const loadInitialData = async () => {
      if (!messageId || !user) return

      try {
        setInitialLoading(true)
        const loadedMessage = await messageService.getById(messageId)

        if (!loadedMessage) {
          toast.error('Message not found')
          navigate(`/messages?tab=${activeTab}`)
          return
        }

        // Check if user has permission to edit (only creator can edit)
        if (loadedMessage.creator.id !== user.id) {
          toast.error('You do not have permission to edit this message')
          navigate(`/messages/${messageId}?tab=${activeTab}`)
          return
        }

        // Only allow editing drafts
        if (loadedMessage.status !== MessageStatus.Draft) {
          toast.error('Only draft messages can be edited')
          navigate(`/messages/${messageId}?tab=${activeTab}`)
          return
        }

        setMessage(loadedMessage)
      } catch (error) {
        console.error('Failed to load message:', error)
        toast.error('Failed to load message')
        navigate(`/messages?tab=${activeTab}`)
      } finally {
        setInitialLoading(false)
      }
    }

    loadInitialData()
  }, [messageId, user, navigate, activeTab])

  const handleSubmit = async (data: MessageFormData, isDraft: boolean) => {
    if (!messageId || !user) return

    setLoading(true)
    if (isDraft) {
      setPrimaryActionLoading(true)
    } else {
      setSecondaryActionLoading(true)
    }

    try {
      if (isDraft) {
        // Just updating the draft
        await messageService.updateMessage(messageId, data, user.id)
        toast.success('Draft updated successfully')
      } else {
        // Publishing: update the message and change status to Published
        await messageService.updateMessage(messageId, data, user.id)
        await messageService.update(messageId, {
          status: MessageStatus.Published,
          readBy: [
            {
              userId: user.id,
              readAt: new Date(),
            },
          ],
        } as Partial<Message>)
        toast.success('Message sent successfully')
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      navigate(`/messages/${messageId}?tab=${activeTab}`)
    } catch (error) {
      console.error('Failed to update message:', error)
      toast.error(isDraft ? 'Failed to update message' : 'Failed to send message')
    } finally {
      setLoading(false)
      setPrimaryActionLoading(false)
      setSecondaryActionLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="spinner" text="Loading message..." />
      </PageContainer>
    )
  }

  if (!message) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title={message.name} titlePrefix="Editing Message:" />

      <MessageForm
        mode="edit"
        initialData={message}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/messages/${messageId}?tab=${activeTab}`)}
        loading={loading}
        primaryActionLoading={primaryActionLoading}
        secondaryActionLoading={secondaryActionLoading}
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
